import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, RefreshCw, Loader2 } from "lucide-react";
import { API } from "@/context/AuthContext";
import { inr } from "@/pages/Dashboard";

const when = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "never");

const CheckRow = ({ r }) => (
  <tr className="border-b border-slate-100" data-testid={`gst-3b-ledger-${r.check}-${r.head.toLowerCase()}`}>
    <td className="py-1.5 text-slate-700">{r.label}</td>
    <td className="font-medium">{r.head}</td>
    <td className="text-right tabular-nums">{inr(r.gstr3b)}</td>
    <td className="text-right tabular-nums">{inr(r.ledger)}</td>
    <td className={`text-right tabular-nums font-semibold ${r.ties ? "text-emerald-700" : "text-rose-700"}`}>
      <span className="inline-flex items-center gap-1 justify-end">{r.ties ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{inr(r.difference)}</span>
    </td>
  </tr>
);

export const GSTR3BLedgerCheck = ({ extractId, report }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    if (!extractId || !report?.itc) return;
    axios.get(`${API}/gst-returns/extracts/${extractId}/ledger-check`).then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Ledger check failed"));
  }, [extractId, report]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/reports/gstr2/gl-balance/refresh`);
      for (let i = 0; i < 150; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const s = (await axios.get(`${API}/reports/gstr2/gl-balance/status`)).data;
        if (s.status !== "running") { if (s.status === "error") toast.error(`Ledger read failed: ${s.error}`); else toast.success("Ledgers re-read from SAP"); break; }
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not refresh the ledgers"); } finally { setBusy(false); }
  };

  if (!report?.itc) return null;
  const st = data?.ledger_status;
  return (
    <Card className={`shadow-sm ${data ? (data.all_tie ? "border-emerald-300" : "border-rose-300") : "border-slate-200"}`} data-testid="gst-3b-ledger-card">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2">GSTR-3B vs Input-GST ledger
            {data && (data.all_tie ? <CheckCircle2 className="h-5 w-5 text-emerald-600" data-testid="gst-3b-ledger-all-tie" /> : <XCircle className="h-5 w-5 text-rose-600" data-testid="gst-3b-ledger-gap" />)}
          </CardTitle>
          <p className="text-[11px] text-slate-500 mt-1" data-testid="gst-3b-ledger-meta">
            Posting date {data?.from} → {data?.to} · ledgers last read {when(st?.finished_at)}{st?.status === "error" ? " (last refresh failed)" : ""} · {data?.ledger_lines?.toLocaleString() ?? "…"} lines, {data?.excluded_utilisation_lines?.toLocaleString() ?? "…"} utilisation / set-off lines left out
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={refresh} disabled={busy} data-testid="gst-3b-ledger-refresh-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}{busy ? "Reading ledgers…" : "Refresh ledger from SAP"}
        </Button>
      </CardHeader>
      <CardContent>
        <table className="w-full text-xs">
          <thead><tr className="text-slate-500 uppercase text-[10px] border-b"><th className="text-left py-1">Check</th><th className="text-left">Head</th><th className="text-right">GSTR-3B</th><th className="text-right">Ledger</th><th className="text-right">Difference</th></tr></thead>
          <tbody>{(data?.rows || []).map((r) => <CheckRow key={`${r.check}-${r.head}`} r={r} />)}</tbody>
        </table>
        {data?.gstr2b_import_igst ? <p className="text-[11px] text-sky-800 mt-2" data-testid="gst-3b-ledger-g2b-note">Import IGST {inr(data.gstr2b_import_igst)} taken from GSTR-2B (customs IGST is booked to Custom Duty Charges, not Input IGST) is left out of the IGST comparison.</p> : null}
        <p className="text-[11px] text-slate-500 mt-2" data-testid="gst-3b-ledger-hint">
          GSTR-2 register synced {when(data?.register_built_at)}.
          {data && !data.all_tie && data.register_built_at && st?.finished_at && st.finished_at > data.register_built_at
            ? " The ledgers were read after the last register sync — documents posted since then show as a gap. Run Sync SAP Data, then Regenerate GSTR-3B."
            : " If postings changed, refresh the ledgers, run Sync SAP Data and Regenerate GSTR-3B so both sides use the same data."}
        </p>
      </CardContent>
    </Card>
  );
};
